<template>
  <v-dialog :value="value" max-width="900" persistent @input="$emit('input', $event)">
    <v-card class="guide-dialog">
      <div class="guide-header primary">
        <h2 class="guide-title">{{ $t("reports.yieldPredictionGuide") }}</h2>
        <v-btn icon dark class="close-btn" @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="guide-content pa-6">
        <div v-if="currentStep === 1" class="step-content">
          <div class="guide-image-wrapper mb-4">
            <img
              :src="`${publicPath}img/reports/yield-prediction/forecasted-yields.png`"
              :alt="$t('reports.forecastedYields')"
              class="guide-image"
            />
          </div>
          <h4 class="step-label mb-2">{{ $t("reports.step1") }}</h4>
          <h4 class="section-title mb-3">{{ $t("reports.whatIsYieldReportTitle") }}</h4>
          <p class="text-body-2 guide-copy">{{ $t("reports.yieldReportDescription") }}</p>
        </div>

        <div v-if="currentStep === 2" class="step-content">
          <div class="guide-image-wrapper mb-4">
            <img
              :src="`${publicPath}img/reports/yield-prediction/satellite-map.png`"
              :alt="$t('reports.satelliteMapView')"
              class="guide-image"
            />
          </div>
          <h4 class="step-label mb-4">{{ $t("reports.step2") }}</h4>
          <h4 class="section-title mb-3">{{ $t("reports.howToGenerateReport") }}</h4>
          <p class="text-body-2 mb-4 guide-copy">{{ $t("reports.creatingYourReportEasy") }}</p>
          <ol class="guide-list">
            <li class="mb-3">
              <strong>{{ $t("reports.selectYourFarm") }}</strong>
              {{ $t("reports.selectYourFarmDescription") }}
            </li>
            <li class="mb-3">
              <strong>{{ $t("reports.selectCropType") }}:</strong>
              {{ $t("reports.selectCropTypeDescription") }}
            </li>
            <li class="mb-3">
              <strong>{{ $t("reports.clickGenerateReport") }}</strong>
              {{ $t("reports.clickGenerateReportDescription") }}
            </li>
          </ol>
        </div>

        <div v-if="currentStep === 3" class="step-content">
          <div class="guide-image-wrapper mb-4">
            <img
              :src="`${publicPath}img/reports/yield-prediction/interpret-graph.png`"
              :alt="$t('reports.yieldTrendsGraph')"
              class="guide-image"
            />
          </div>
          <p class="step-label mb-4">{{ $t("reports.step3") }}</p>
          <h4 class="section-title mb-3">{{ $t("reports.howToInterpretReport") }}</h4>
          <p class="text-body-2 mb-4 guide-copy">{{ $t("reports.interpretReportIntro") }}</p>

          <div v-for="line in lineDescriptions" :key="line.key" class="line-explanation mb-3">
            <div class="d-flex align-start">
              <span :class="['legend-line', line.className]" />
              <div>
                <p class="text-body-2 font-weight-medium mb-1" :style="{ color: line.color }">
                  {{ $t(line.title) }}
                </p>
                <p class="text-body-2 guide-copy">{{ $t(line.description) }}</p>
              </div>
            </div>
          </div>

          <p class="text-body-2 mb-2 guide-copy">
            <strong>{{ $t("reports.highPredictedYieldsLabel") }}</strong>
            {{ $t("reports.highPredictedYieldsDescription") }}
          </p>
          <p class="text-body-2 guide-copy">
            <strong>{{ $t("reports.lowPredictedYieldsLabel") }}</strong>
            {{ $t("reports.lowPredictedYieldsDescription") }}
          </p>
        </div>

        <div v-if="currentStep === 4" class="step-content">
          <h4 class="mb-4 font-weight-medium">{{ $t("reports.tip") }}:</h4>
          <p class="text-body-2 guide-copy">{{ $t("reports.tipDescription") }}</p>
        </div>
      </v-card-text>

      <v-card-actions class="guide-footer px-6 py-4">
        <v-btn outlined color="primary" class="mr-2" @click="close">
          {{ $t("close") }}
        </v-btn>
        <v-spacer />
        <v-btn v-if="currentStep > 1" outlined color="primary" class="mr-2" @click="currentStep -= 1">
          {{ $t("back") }}
        </v-btn>
        <v-btn v-if="currentStep < 4" color="primary" @click="currentStep += 1">
          {{ $t("next") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "YieldPredictionGuideDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      currentStep: 1,
      publicPath: process.env.BASE_URL || "/",
      lineDescriptions: [
        {
          key: "actual",
          className: "blue-line",
          color: "#1976d2",
          title: "reports.blueLineActualYields",
          description: "reports.blueLineDescription",
        },
        {
          key: "predicted",
          className: "red-line dashed",
          color: "#f44336",
          title: "reports.redLinePredictedYields",
          description: "reports.redLineDescription",
        },
        {
          key: "average",
          className: "brown-line dotted",
          color: "#8d6e63",
          title: "reports.brownLineAverageYields",
          description: "reports.brownLineDescription",
        },
      ],
    };
  },
  watch: {
    value(open) {
      if (!open) this.currentStep = 1;
    },
  },
  methods: {
    close() {
      this.currentStep = 1;
      this.$emit("input", false);
    },
  },
};
</script>

<style lang="scss" scoped>
.guide-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
}

.guide-title {
  margin: 0;
  color: white;
  font-size: 20px;
  font-weight: 500;
}

.guide-content {
  max-height: 65vh;
  overflow-y: auto;
  background: white;
}

.step-content {
  min-height: 450px;
}

.step-label {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.section-title {
  color: #4caf50;
  font-size: 16px;
  font-weight: 500;
}

.guide-copy,
.guide-list li {
  line-height: 1.6;
}

.guide-list {
  padding-left: 20px;
}

.guide-image-wrapper {
  display: flex;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #f9f9f9;
}

.guide-image {
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: contain;
}

.line-explanation .d-flex {
  gap: 10px;
}

.legend-line {
  display: inline-block;
  width: 24px;
  min-width: 24px;
  height: 2px;
  margin-top: 8px;
}

.blue-line {
  background: #2196f3;
}

.red-line.dashed {
  height: 0;
  border-top: 2px dashed #f44336;
}

.brown-line.dotted {
  height: 0;
  border-top: 2px dotted #8d6e63;
}

.guide-footer {
  border-top: 1px solid #e0e0e0;
  background: white;
}
</style>
